import {MOON_100, MOON_500} from '@wandb/weave/common/css/color.styles';
import {Button} from '@wandb/weave/components/Button';
import {Select} from '@wandb/weave/components/Form/Select';
import {WaveLoader} from '@wandb/weave/components/Loaders/WaveLoader';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import {toast} from 'react-toastify';

import {ReusableDrawer} from '../../ReusableDrawer';
import {ScorerFormRef} from '../MonitorsPage/MonitorFormDrawer';
import {LLMAsAJudgeScorerForm} from '../MonitorsPage/ScorerForms/LLMAsAJudgeScorerForm';
import {LLMDropdownLoaded} from '../PlaygroundPage/PlaygroundChat/LLMDropdown';
import {useGetTraceServerClientContext} from '../wfReactInterface/traceServerClientContext';
import {ObjectVersionSchema} from '../wfReactInterface/wfDataModelHooksInterface';
import {refStringToName} from './common';
import {
  LabeledTextAreaWithVariableInsertion,
  LabeledTextField,
  LoadingSelect,
} from './components';
import {BORDER_COLOR} from './constants';
import {useEvaluationPlaygroundPageContext} from './context';
import {useDatasetColumnsContext} from './DatasetColumnsContext';
import {clientBound, hookify} from './hooks';
import {Column, ConfigSection, Row} from './layout';
import {getAvailableFields, validatePromptVariables} from './promptValidation';
import {
  getLatestScorerRefs,
  getObjByRef,
  getSimplifiedLLMAsAJudgeScorer,
  isLLMAsAJudgeScorer,
  publishSimplifiedLLMAsAJudgeScorer,
} from './query';
import {defaultScorerConfigPayload} from './state';
import {SimplifiedLLMAsAJudgeScorer} from './types';
import {VersionedObjectPicker} from './VersionedObjectPicker';

const newScorerOption = {
  label: 'New Scorer',
  value: 'new-scorer',
};

// Create the hook for fetching scorer refs
// This wraps the async query function in a React hook that manages loading/error states
const useLatestScorerRefs = clientBound(hookify(getLatestScorerRefs));
const useSimplifiedScorer = clientBound(
  hookify(getSimplifiedLLMAsAJudgeScorer)
);
const useIsLLMAsAJudgeScorer = clientBound(hookify(isLLMAsAJudgeScorer));

// Separate component for each scorer row to properly memoize callbacks
const ScorerRow: React.FC<{
  entity: string;
  project: string;
  scorer: {originalSourceRef: string | null};
  scorerNdx: number;
  updateScorerRef: (scorerNdx: number, ref: string | null) => void;
  deleteScorer: (scorerNdx: number) => void;
  setCurrentlyEditingScorerNdx: (
    ndx: number,
    simplifiedConfig?: SimplifiedLLMAsAJudgeScorer
  ) => void;
  scorerRefsQuery: ReturnType<typeof useLatestScorerRefs>;
  onRegisterSaveHandler: (
    scorerNdx: number,
    handler: (() => Promise<string>) | null
  ) => void;
  onValidationErrorChange?: (scorerNdx: number, hasError: boolean) => void;
}> = ({
  entity,
  project,
  scorer,
  scorerNdx,
  updateScorerRef,
  deleteScorer,
  setCurrentlyEditingScorerNdx,
  scorerRefsQuery,
  onRegisterSaveHandler,
  onValidationErrorChange,
}) => {
  const [isExpanded, setIsExpanded] = useState(true);

  const handleRefChange = useCallback(
    (ref: string | null) => {
      updateScorerRef(scorerNdx, ref);
    },
    [scorerNdx, updateScorerRef]
  );

  const handleDelete = useCallback(() => {
    deleteScorer(scorerNdx);
  }, [scorerNdx, deleteScorer]);

  const handleSaveStateChange = useCallback(
    (canSave: boolean, handler: () => Promise<string>) => {
      // Register/unregister with parent
      onRegisterSaveHandler(scorerNdx, canSave ? handler : null);
    },
    [scorerNdx, onRegisterSaveHandler]
  );

  // Only validate the initial scorer ref, not subsequent changes
  const [initialRef] = useState(scorer.originalSourceRef);

  // Only query if we have an initial ref to validate
  const isLLMAsAJudgeScorerQuery = useIsLLMAsAJudgeScorer(initialRef || '');

  useEffect(() => {
    // Delete if query completes and it's not an LLMJudge scorer
    if (
      initialRef &&
      !isLLMAsAJudgeScorerQuery.loading &&
      isLLMAsAJudgeScorerQuery.data === false
    ) {
      toast.error('Removing non LLMJudge Scorer');
      handleDelete();
    }
  }, [
    isLLMAsAJudgeScorerQuery.loading,
    isLLMAsAJudgeScorerQuery.data,
    initialRef,
    handleDelete,
  ]);

  // Show loading state for individual dropdowns if query is loading
  if (scorerRefsQuery.loading) {
    return (
      <Row style={{alignItems: 'center', gap: '8px'}}>
        <Button icon="chevron-next" variant="ghost" disabled />
        <div style={{flex: 1}}>
          <LoadingSelect />
        </div>
        <Button variant="primary" disabled>
          Save
        </Button>
        <Button icon="close" variant="ghost" disabled />
      </Row>
    );
  }

  return (
    <Column
      style={{
        borderRadius: '6px',
        boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
      }}>
      <Row
        style={{
          position: 'sticky',
          top: -2,
          zIndex: 1,
          alignItems: 'center',
          gap: '8px',
          backgroundColor: '#fff',
          borderTop: `1px solid ${BORDER_COLOR}`,
          borderLeft: `1px solid ${BORDER_COLOR}`,
          borderRight: `1px solid ${BORDER_COLOR}`,
          borderTopLeftRadius: '6px',
          borderTopRightRadius: isExpanded ? '6px' : '6px',
          borderBottomLeftRadius: isExpanded ? '0px' : '6px',
          borderBottomRightRadius: isExpanded ? '0px' : '6px',
          borderBottom: `1px solid ${BORDER_COLOR}`,
          padding: '8px',
        }}>
        <Button
          icon={isExpanded ? 'chevron-down' : 'chevron-next'}
          variant="ghost"
          onClick={() => setIsExpanded(!isExpanded)}
          tooltip={
            isExpanded
              ? 'Collapse scorer configuration'
              : 'Expand scorer configuration'
          }
        />
        <div style={{flex: 1}}>
          <VersionedObjectPicker
            entity={entity}
            project={project}
            objectType="scorer"
            selectedRef={scorer.originalSourceRef}
            onRefChange={handleRefChange}
            latestObjectRefs={scorerRefsQuery.data ?? []}
            loading={scorerRefsQuery.loading}
            newOptions={[{label: 'New Scorer', value: 'new-scorer'}]}
            allowNewOption={true}
          />
        </div>

        <Button icon="close" variant="ghost" onClick={handleDelete} />
      </Row>
      {isExpanded && (
        <SimplifiedScorerConfig
          entity={entity}
          project={project}
          scorerRef={scorer.originalSourceRef}
          scorerNdx={scorerNdx}
          onScorerSaved={updateScorerRef}
          onOpenAdvancedSettings={setCurrentlyEditingScorerNdx}
          onSaveStateChange={handleSaveStateChange}
          onValidationErrorChange={onValidationErrorChange}
        />
      )}
    </Column>
  );
};

interface SimplifiedScorerConfigProps {
  entity: string;
  project: string;
  scorerRef: string | null;
  scorerNdx: number;
  onScorerSaved: (scorerNdx: number, newRef: string) => void;
  onOpenAdvancedSettings: (
    scorerNdx: number,
    simplifiedConfig: SimplifiedLLMAsAJudgeScorer
  ) => void;
  onSaveStateChange: (
    canSave: boolean,
    saveHandler: () => Promise<string>
  ) => void;
}

/**
 * Simplified scorer configuration form for basic LLM-as-a-Judge scorers.
 * Provides an inline form for scorers that don't require advanced configuration.
 */
const SimplifiedScorerConfig: React.FC<
  SimplifiedScorerConfigProps & {
    onValidationErrorChange?: (scorerNdx: number, hasError: boolean) => void;
  }
> = ({
  entity,
  project,
  scorerRef,
  scorerNdx,
  onScorerSaved,
  onOpenAdvancedSettings,
  onSaveStateChange,
  onValidationErrorChange,
}) => {
  const client = useGetTraceServerClientContext()();
  // Local state for the simplified form
  const [config, setConfig] = useState<SimplifiedLLMAsAJudgeScorer>(
    defaultScorerConfigPayload
  );

  const [isSaving, setIsSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(!scorerRef); // New scorers start with unsaved changes
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);

  const simplifiedScorerQuery = useSimplifiedScorer(
    entity,
    project,
    scorerRef ?? ''
  );

  // Get dataset columns from context only - this works for both unsaved and saved datasets
  const {columns: contextColumns} = useDatasetColumnsContext();
  const datasetColumns = contextColumns;

  // Reset unsaved changes immediately when ref changes to an existing scorer
  useEffect(() => {
    if (scorerRef) {
      // Immediately reset unsaved changes when switching to an existing scorer
      setHasUnsavedChanges(false);
    } else {
      // For new scorers, mark as having unsaved changes
      setHasUnsavedChanges(true);
    }
  }, [scorerRef]);

  // Load existing scorer configuration when ref changes
  useEffect(() => {
    if (
      scorerRef &&
      simplifiedScorerQuery.data !== null &&
      !simplifiedScorerQuery.loading
    ) {
      setConfig(simplifiedScorerQuery.data);
      setInitialLoadComplete(true);
    } else if (!scorerRef) {
      // For new scorers, mark as loaded immediately
      setInitialLoadComplete(true);
    }
    setInitialLoadComplete(!simplifiedScorerQuery.loading);
  }, [scorerRef, simplifiedScorerQuery.data, simplifiedScorerQuery.loading]);

  // Track unsaved changes
  const updateConfig = useCallback(
    (updates: Partial<SimplifiedLLMAsAJudgeScorer>) => {
      setConfig(prev => ({...prev, ...updates}));
      setHasUnsavedChanges(true);
    },
    []
  );

  // Reset to original config (either loaded data or default)
  const handleReset = useCallback(() => {
    if (scorerRef && simplifiedScorerQuery.data) {
      // Reset to loaded data for existing scorers
      setConfig(simplifiedScorerQuery.data);
    } else {
      // Reset to default for new scorers
      setConfig(defaultScorerConfigPayload);
    }
    setHasUnsavedChanges(false);
  }, [scorerRef, simplifiedScorerQuery.data]);

  // Validation: name and prompt are required, LLM is automatically selected
  const isValid =
    config.name.trim() !== '' &&
    config.prompt.trim() !== '' &&
    config.llmModelId.trim() !== '';

  // Prompt validation: check that all variables in the prompt are valid
  const availableFields = getAvailableFields(datasetColumns);
  const promptValidation = validatePromptVariables(
    config.prompt,
    availableFields
  );

  const promptError = promptValidation.isValid
    ? ''
    : promptValidation.invalidVariables.length === 1
      ? `Invalid variable '${promptValidation.invalidVariables[0]}'`
      : `Invalid variables: ${promptValidation.invalidVariables
          .map(v => `'${v}'`)
          .join(', ')}`;

  // Only show validation errors if we have dataset columns loaded
  const shouldShowPromptError = promptError;

  useEffect(() => {
    onValidationErrorChange?.(scorerNdx, !!shouldShowPromptError);
  }, [onValidationErrorChange, scorerNdx, shouldShowPromptError]);

  // Overall validation includes prompt validation, but only if we have dataset columns
  const isFullyValid =
    isValid && (datasetColumns.length === 0 || promptValidation.isValid);

  const handleSave = useCallback(async () => {
    if (!isFullyValid || isSaving) return '';

    setIsSaving(true);
    try {
      // Save the scorer directly
      const scorerRef = await publishSimplifiedLLMAsAJudgeScorer(
        client,
        entity,
        project,
        config
      );

      if (scorerRef) {
        setHasUnsavedChanges(false);
        onScorerSaved(scorerNdx, scorerRef);
        return scorerRef; // Return the ref for the save-all handler
      }
      return '';
    } catch (error) {
      console.error('Failed to save scorer:', error);
      // TODO: Add proper error handling
      return '';
    } finally {
      setIsSaving(false);
    }
  }, [
    isFullyValid,
    isSaving,
    client,
    entity,
    project,
    config,
    onScorerSaved,
    scorerNdx,
  ]);

  // Check if this scorer qualifies for simplified config
  const qualifies = !scorerRef || simplifiedScorerQuery.data !== null;
  const isLoading = scorerRef && simplifiedScorerQuery.loading;

  // For new scorers, if LLM is auto-selected, mark as having unsaved changes
  useEffect(() => {
    if (!scorerRef && config.llmModelId && initialLoadComplete) {
      setHasUnsavedChanges(true);
    }
  }, [scorerRef, config.llmModelId, initialLoadComplete]);

  // Notify parent about save state changes
  useEffect(() => {
    const canSave = hasUnsavedChanges && isFullyValid && !isSaving;
    // Pass the handler directly since it already returns a Promise<string>
    onSaveStateChange(canSave, canSave ? handleSave : (null as any));
  }, [
    hasUnsavedChanges,
    isFullyValid,
    isSaving,
    handleSave,
    onSaveStateChange,
  ]);

  // Show loading state while checking if scorer qualifies
  if (isLoading || !initialLoadComplete) {
    return (
      <div
        style={{
          padding: '12px',
          backgroundColor: MOON_100,
          borderBottomLeftRadius: '6px',
          borderBottomRightRadius: '6px',
          fontSize: '14px',
          color: MOON_500,
          textAlign: 'center',
        }}>
        Loading scorer configuration...
      </div>
    );
  }

  // Show advanced configuration notice for complex scorers
  if (!qualifies) {
    return (
      <div
        style={{
          padding: '12px',
          backgroundColor: MOON_100,
          borderBottomLeftRadius: '6px',
          borderBottomRightRadius: '6px',
          borderLeft: `1px solid ${BORDER_COLOR}`,
          borderRight: `1px solid ${BORDER_COLOR}`,
          borderBottom: `1px solid ${BORDER_COLOR}`,
          fontSize: '14px',
          color: MOON_500,
          fontStyle: 'italic',
        }}>
        This scorer uses advanced configuration.{' '}
        <Button
          variant="ghost"
          size="small"
          onClick={() => onOpenAdvancedSettings(scorerNdx, config)}
          style={{padding: '2px 8px', fontSize: '14px'}}>
          Edit settings →
        </Button>
      </div>
    );
  }

  return (
    <Column style={{}}>
      <Column
        style={{
          padding: '12px',
          backgroundColor: '#fff',
          borderLeft: `1px solid ${BORDER_COLOR}`,
          borderRight: `1px solid ${BORDER_COLOR}`,
          gap: '16px',
        }}>
        <LabeledTextField
          label="Name"
          placeholder="Scorer name"
          value={config.name}
          onChange={value => updateConfig({name: value})}
        />

        <div style={{display: 'flex', flexDirection: 'column', gap: '8px'}}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              fontWeight: 600,
              fontSize: '16px',
            }}>
            <span>Type</span>
          </div>
          <Select
            value={{
              label: config.scoreType === 'boolean' ? 'Boolean' : 'Number',
              value: config.scoreType,
            }}
            options={[
              {label: 'Boolean', value: 'boolean'},
              {label: 'Number', value: 'number'},
            ]}
            onChange={option =>
              updateConfig({
                scoreType: option?.value as 'boolean' | 'number',
              })
            }
          />
        </div>

        <div style={{display: 'flex', flexDirection: 'column', gap: '8px'}}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              fontWeight: 600,
              fontSize: '16px',
            }}>
            <span>LLM-as-a-judge model</span>
          </div>
          <Tailwind>
            <LLMDropdownLoaded
              className="w-full"
              hideSavedModels
              value={config.llmModelId || ''}
              isTeamAdmin={false}
              direction={{horizontal: 'right'}}
              selectFirstAvailable={true}
              onChange={(modelValue: string) => {
                updateConfig({llmModelId: modelValue});
              }}
            />
          </Tailwind>
        </div>

        <LabeledTextAreaWithVariableInsertion
          label="Scoring prompt"
          placeholder="Enter the scoring prompt (e.g., 'Is the response helpful and accurate?')"
          value={config.prompt}
          onChange={e => updateConfig({prompt: e.target.value})}
          rows={10}
          error={shouldShowPromptError ? promptError : undefined}
          availableVariables={availableFields}
        />
      </Column>

      {/* Footer with reset and save buttons */}
      <Row
        style={{
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '8px',
          backgroundColor: '#fff',
          border: `1px solid ${BORDER_COLOR}`,
          borderBottomLeftRadius: '6px',
          borderBottomRightRadius: '6px',
        }}>
        <Button
          variant="ghost"
          onClick={handleReset}
          disabled={!hasUnsavedChanges}
          tooltip="Reset changes">
          Reset
        </Button>
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={!isFullyValid || !hasUnsavedChanges || isSaving}
          tooltip="Save scorer configuration">
          {isSaving ? 'Saving...' : 'Save'}
        </Button>
      </Row>
    </Column>
  );
};

/**
 * Main scorer configuration section component.
 * Manages the list of scorers with simplified inline configuration
 * and advanced configuration drawer for complex scorers.
 */
export interface ScorersConfigSectionRef {
  saveAllUnsavedAndGetRefs: () => Promise<string[]>;
}

export const ScorersConfigSection = React.forwardRef<
  ScorersConfigSectionRef,
  {
    entity: string;
    project: string;
    onValidationChanged?: (hasErrors: boolean) => void;
  }
>(({entity, project, onValidationChanged}, ref) => {
  const {config, editConfig} = useEvaluationPlaygroundPageContext();
  const scorerRefsQuery = useLatestScorerRefs(entity, project);

  const [currentlyEditingScorerNdx, setCurrentlyEditingScorerNdx] = useState<
    number | null
  >(null);

  // Track simplified config data to pass to drawer
  const [pendingSimplifiedConfig, setPendingSimplifiedConfig] =
    useState<SimplifiedLLMAsAJudgeScorer | null>(null);

  const scorers = useMemo(() => {
    return config.evaluationDefinition.properties.scorers;
  }, [config]);

  // Keep track of save handlers for each scorer
  const saveHandlersRef = useRef<Map<number, () => Promise<string>>>(new Map());

  // Keep track of newly saved refs to avoid stale closure issue
  const newlySavedRefsRef = useRef<Map<number, string>>(new Map());

  const addScorer = useCallback(() => {
    editConfig(draft => {
      draft.evaluationDefinition.properties.scorers.push({
        originalSourceRef: null,
      });
    });
  }, [editConfig]);

  // Track validation errors per scorer and bubble up aggregate state
  const [scorerValidationErrors, setScorerValidationErrors] = useState<
    Record<number, boolean>
  >({});
  const handleScorerValidationChange = useCallback(
    (scorerNdx: number, hasError: boolean) => {
      setScorerValidationErrors(prev => ({...prev, [scorerNdx]: hasError}));
    },
    []
  );
  useEffect(() => {
    const hasErrors = Object.values(scorerValidationErrors).some(Boolean);
    onValidationChanged?.(hasErrors);
  }, [scorerValidationErrors, onValidationChanged]);

  const scorerOptions = useMemo(() => {
    return [
      {
        label: 'Create new scorer',
        options: [newScorerOption],
      },
      {
        label: 'Load existing scorer',
        options:
          scorerRefsQuery.data?.map(ref => ({
            label: refStringToName(ref),
            value: ref,
          })) ?? [],
      },
    ];
  }, [scorerRefsQuery.data]);

  const deleteScorer = useCallback(
    (scorerNdx: number) => {
      editConfig(draft => {
        draft.evaluationDefinition.properties.scorers.splice(scorerNdx, 1);
      });
      // Clean up save handler when deleting
      saveHandlersRef.current.delete(scorerNdx);
    },
    [editConfig]
  );

  const updateScorerRef = useCallback(
    (scorerNdx: number, ref: string | null) => {
      editConfig(draft => {
        // Don't update if the ref hasn't changed
        const currentRef =
          draft.evaluationDefinition.properties.scorers[scorerNdx]
            ?.originalSourceRef;
        if (ref === currentRef) {
          return;
        }

        if (ref === 'new-scorer' || ref === null) {
          // Reset to empty scorer
          draft.evaluationDefinition.properties.scorers[
            scorerNdx
          ].originalSourceRef = null;
        } else {
          // Set the selected scorer ref
          draft.evaluationDefinition.properties.scorers[
            scorerNdx
          ].originalSourceRef = ref;
        }
      });
    },
    [editConfig]
  );

  const registerSaveHandler = useCallback(
    (scorerNdx: number, handler: (() => Promise<string>) | null) => {
      if (handler) {
        saveHandlersRef.current.set(scorerNdx, handler);
      } else {
        saveHandlersRef.current.delete(scorerNdx);
      }
    },
    []
  );

  // Handle opening advanced settings (either from gear or from simplified form)
  const handleOpenAdvancedSettings = useCallback(
    (
      scorerNdx: number | null,
      simplifiedConfig?: SimplifiedLLMAsAJudgeScorer
    ) => {
      if (simplifiedConfig) {
        // Store the simplified config to pass to the drawer
        setPendingSimplifiedConfig(simplifiedConfig);
      }
      setCurrentlyEditingScorerNdx(scorerNdx);
    },
    []
  );

  // Expose save all method via ref
  useImperativeHandle(
    ref,
    () => ({
      saveAllUnsavedAndGetRefs: async () => {
        // Clear previous saves
        newlySavedRefsRef.current.clear();

        // Save all unsaved scorers and capture their refs
        const savePromises = Array.from(saveHandlersRef.current.entries()).map(
          async ([idx, handler]) => {
            const ref = await handler();
            if (ref) {
              newlySavedRefsRef.current.set(idx, ref);
            }
          }
        );
        await Promise.all(savePromises);

        // Build the list of all refs, using newly saved ones where available
        const allRefs: string[] = [];
        scorers.forEach((scorer, idx) => {
          const newRef = newlySavedRefsRef.current.get(idx);
          const ref = newRef || scorer.originalSourceRef;
          if (ref) {
            allRefs.push(ref);
          }
        });

        return allRefs;
      },
      // hasValidationErrors: () => Object.values(scorerValidationErrors).some(Boolean),
    }),
    [scorers]
  );

  return (
    <ConfigSection
      title="Scorers"
      icon="type-number-alt"
      headerAction={
        <Button
          icon="add-new"
          variant="ghost"
          onClick={() => {
            addScorer();
          }}>
          Add Scorer
        </Button>
      }>
      <Column style={{gap: '8px'}}>
        {scorers.map((scorer, scorerNdx) => {
          let selectedOption = newScorerOption;
          const options = [...scorerOptions];
          if (scorer.originalSourceRef) {
            const name = refStringToName(scorer.originalSourceRef);
            selectedOption = {
              label: name,
              value: scorer.originalSourceRef,
            };
            // Add current selection if it's not in the list (e.g., from another project)
            const allOptions = options.flatMap(group => group.options);
            if (
              !allOptions.find(opt => opt.value === scorer.originalSourceRef)
            ) {
              // Add to the existing scorers group
              options[1].options.unshift(selectedOption);
            }
          }

          return (
            <ScorerRow
              key={scorerNdx}
              entity={entity}
              project={project}
              scorer={scorer}
              scorerNdx={scorerNdx}
              updateScorerRef={updateScorerRef}
              deleteScorer={deleteScorer}
              setCurrentlyEditingScorerNdx={handleOpenAdvancedSettings}
              scorerRefsQuery={scorerRefsQuery}
              onRegisterSaveHandler={registerSaveHandler}
              onValidationErrorChange={handleScorerValidationChange}
            />
          );
        })}
        <ScorerDrawer
          entity={entity}
          project={project}
          open={currentlyEditingScorerNdx !== null}
          onClose={(newScorerRef?: string) => {
            if (newScorerRef && currentlyEditingScorerNdx !== null) {
              editConfig(draft => {
                draft.evaluationDefinition.properties.scorers[
                  currentlyEditingScorerNdx
                ].originalSourceRef = newScorerRef;
              });
            }
            setCurrentlyEditingScorerNdx(null);
            setPendingSimplifiedConfig(null); // Clear pending config
          }}
          initialScorerRef={
            currentlyEditingScorerNdx !== null
              ? (scorers[currentlyEditingScorerNdx]?.originalSourceRef ??
                undefined)
              : undefined
          }
          pendingSimplifiedConfig={pendingSimplifiedConfig}
        />
      </Column>
    </ConfigSection>
  );
});

const emptyScorer = (entity: string, project: string): ObjectVersionSchema => ({
  scheme: 'weave',
  weaveKind: 'object',
  entity,
  project,
  objectId: '',
  versionHash: '',
  path: '',
  versionIndex: 0,
  baseObjectClass: 'LLMAsAJudgeScorer',
  createdAtMs: Date.now(),
  val: {_type: 'LLMAsAJudgeScorer'},
});

const useScorer = clientBound(hookify(getObjByRef));

/**
 * Drawer component for advanced scorer configuration.
 * Opened when a scorer requires configuration beyond the simplified form.
 */
const ScorerDrawer: React.FC<{
  entity: string;
  project: string;
  open: boolean;
  onClose: (newScorerRef?: string) => void;
  initialScorerRef?: string;
  pendingSimplifiedConfig?: SimplifiedLLMAsAJudgeScorer | null;
}> = ({
  entity,
  project,
  open,
  onClose,
  initialScorerRef,
  pendingSimplifiedConfig,
}) => {
  const scorerFormRef = useRef<ScorerFormRef | null>(null);
  const onSave = useCallback(async () => {
    const newScorerRef = await scorerFormRef.current?.saveScorer();
    onClose(newScorerRef);
  }, [onClose]);

  const scorerQuery = useScorer(initialScorerRef);

  const scorerObj = useMemo(() => {
    let baseScorer;
    if (initialScorerRef) {
      baseScorer = scorerQuery.data ?? emptyScorer(entity, project);
    } else {
      baseScorer = emptyScorer(entity, project);
    }

    return baseScorer;
  }, [entity, initialScorerRef, project, scorerQuery.data]);

  if (scorerQuery.loading) {
    return <WaveLoader size="small" />;
  }

  if (scorerQuery.error) {
    // TODO (EvalPlayground MVP): Handle error state
    return null;
  }

  return (
    <ReusableDrawer
      open={open}
      onClose={() => onClose()}
      title="Scorer"
      onSave={onSave}>
      <Tailwind>
        {/* TODO: Fix this negative margin when we fix up the LLM-as-a-judge scorer form */}
        <div className="-mx-[20px] -mt-[34px]">
          <LLMAsAJudgeScorerForm
            key={`${initialScorerRef || 'new'}-${JSON.stringify(
              pendingSimplifiedConfig
            )}`}
            ref={scorerFormRef}
            scorer={scorerObj}
            onValidationChange={() => {
              // Pass
            }}
          />
        </div>
      </Tailwind>
    </ReusableDrawer>
  );
};
